import { computed, watch } from 'vue';
import { Action, Product, Tracker } from '@studyportals/datalake-event-tracker';
import { InvalidCampaignPropertyDetails } from '@/models/error.interface';
import { WindowNavigator } from '@/models/loading.interface';
import {
	CampaignFeaturesForTracking, CampaignSaveLoadLabels, CampaignSaveLoadOutcomes, CampaignLoadingSpeedLabels
} from '@/models/tracking.interface';
import { IRouter } from '@/models/router/router.interface';
import rollbarOperator from '@/infrastructure/rollbar-operator';
import store from '@/store';

class RedshiftTracker {
	private dataLakeEventTracker: Tracker;
	private tabSwitchStartLabel = 'tab_switch_start';
	private tabSwitchEndLabel = 'tab_switch_end';
	private tabSwitchTotalLabel = 'tab_switch_total';

	private get userId(): number {
		const element = document.querySelector('.js-campaignEditorPlaceholder') as HTMLElement;
		if (!element?.dataset.userId) {
			return 0;
		}

		return parseInt(element.dataset.userId);
	}

	private get networkSpeed(): number {
		const navigator = window.navigator as WindowNavigator;
		if (!navigator?.connection?.downlink) {
			return 0;
		}

		return navigator.connection.downlink;
	}

	private snakeCaseInvalidCampaignPropertyTitle = computed((): string => {
		const snakeCaseCategory = `category_${this.invalidCampaignPropertyDetails.value.category}`
			.toLowerCase()
			.replace(/-/g, '_')
			.replace(/ /g, '_');
		const snakeCaseValue = this.invalidCampaignPropertyDetails.value.value
			.toLowerCase()
			.replace(/[()]/g, '')
			.replace(/[- ,.:;+]/g, '_')
			.replace(/__/g, '_');

		if (!snakeCaseValue) {
			return snakeCaseCategory;
		}

		return `${snakeCaseCategory}_value_${snakeCaseValue}`;
	});

	private routeNameAsSnakeCase = computed((): string => {
		if (this.isCampaignCreation.value) {
			return 'create';
		}

		return this.router.value.current.replace(/-/g, '_');
	});

	private invalidCampaignPropertyDetails = computed((): InvalidCampaignPropertyDetails => {
		return store.getters.invalidCampaignPropertyDetails();
	});

	private campaignId = computed((): number => {
		return this.router.value.props.id;
	});

	private router = computed((): IRouter => {
		return store.getters.router();
	});

	private loadCount = computed((): number => {
		return store.getters.loadCount();
	});

	public isCampaignCreation = computed((): boolean => {
		return store.getters.isCampaignCreation();
	});

	constructor() {
		this.dataLakeEventTracker = new Tracker(Product.CAMPAIGN_EDITOR, false, true);
	}

	public prepareForLoadingSpeedTracking(): void {
		watch(this.loadCount, (value) => {
			this.trackLoadingSpeed(value === 1);
		});
	}

	public markTabSwitchStart(): void {
		if (!this.isPerformanceFunctionalityAvailable()) {
			return;
		}

		window.performance.mark(this.tabSwitchStartLabel);
	}

	public trackUnsupportedCampaignLoad(): void {
		this.trackEvent({
			action: Action.NON_INTERACTIVE,
			label: `${this.snakeCaseInvalidCampaignPropertyTitle.value}@v2_id_${this.campaignId.value}`,
			property: 'unsupported_campaign_load'
		});
	}

	public trackProcessOutcome(featureName: CampaignFeaturesForTracking, isSave: boolean, isSuccessful: boolean): void {
		this.trackEvent({
			action: Action.NON_INTERACTIVE,
			label: `${featureName}@v2_tab_${this.routeNameAsSnakeCase.value}_id_${this.campaignId.value}`,
			property: isSave ? CampaignSaveLoadLabels.SAVE : CampaignSaveLoadLabels.LOAD,
			value: isSuccessful ? CampaignSaveLoadOutcomes.SUCCESS : CampaignSaveLoadOutcomes.FAILURE
		});
	}

	private trackLoadingSpeed(isInitialLoad: boolean): void {
		if (!this.isPerformanceFunctionalityAvailable()) {
			return;
		}

		const firstLabelPart = `speed_${isInitialLoad ? CampaignLoadingSpeedLabels.INITIAL_LOAD : CampaignLoadingSpeedLabels.TAB_SWITCH}`;
		const duration = isInitialLoad ? window.performance.now() : this.determineTabSwitchDuration();
		if (!duration) {
			return;
		}

		this.trackEvent({
			action: Action.NON_INTERACTIVE,
			label: `${firstLabelPart}@v2_tab_${this.routeNameAsSnakeCase.value}_id_${this.campaignId.value}`,
			property: `${this.userId}_${this.networkSpeed}`,
			value: Math.round(duration)
		});
	}

	private determineTabSwitchDuration(): number {
		if (!this.isPerformanceFunctionalityAvailable() ||
			!window.performance.getEntriesByName(this.tabSwitchStartLabel).length) {
			return 0;
		}

		window.performance.mark(this.tabSwitchEndLabel);
		const measurement = window.performance.measure(
			this.tabSwitchTotalLabel,
			this.tabSwitchStartLabel,
			this.tabSwitchEndLabel
		);
		return measurement.duration;
	}

	// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
	private trackEvent(payload: any): void {
		const tracker = this.dataLakeEventTracker as any;
		if (!tracker.dataLayerTracker) {
			return;
		}

		try {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			this.dataLakeEventTracker.trackStructuredEvent(payload);
		} catch (error) {
			rollbarOperator.triggerError('CampaignSaveLoadProcessTracker.trackEvent: could not track event', error);
		}
	}

	private isPerformanceFunctionalityAvailable(): boolean {
		return (
			typeof window.performance?.now === 'function' &&
			typeof window.performance.mark === 'function' &&
			typeof window.performance.clearMarks === 'function' &&
			typeof window.performance.clearMeasures === 'function' &&
			typeof window.performance.measure === 'function'
		);
	}
}

export default new RedshiftTracker();
